import React from 'react';
import type { AnnotationShape } from '../types';
import { RectShape } from './shapes/RectShape';
import { EllipseShape } from './shapes/EllipseShape';
import { LineShape } from './shapes/LineShape';
import { ArrowShape } from './shapes/ArrowShape';
import { FreeformShape } from './shapes/FreeformShape';
import { TextShape } from './shapes/TextShape';
import { getShapeBounds } from '../hooks/useShapeResize';
import { computeRotationTransform } from '../utils/geometry';

interface SvgCanvasProps {
  shapes: AnnotationShape[];
  drawingShape?: AnnotationShape | null;
  isEditor?: boolean;
  selectedIds?: Set<string>;
  containerWidth?: number;
  containerHeight?: number;
  children?: React.ReactNode;
  onPointerDown?: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerMove?: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerUp?: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerLeave?: (e: React.PointerEvent<SVGSVGElement>) => void;
}

/**
 * For line/arrow shapes with rotation, compute rotated endpoints directly in screen space
 * to avoid SVG transform blur artifacts with vectorEffect="non-scaling-stroke".
 */
function rotateLineEndpoints<T extends AnnotationShape & { type: 'line' | 'arrow' }>(
  shape: T,
  rotation: number,
  containerWidth: number,
  containerHeight: number,
): T {
  const bounds = getShapeBounds(shape);
  const cxNorm = bounds.x + bounds.width / 2;
  const cyNorm = bounds.y + bounds.height / 2;

  // Rotate in screen space then convert back
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  function rotPt(nx: number, ny: number): { x: number; y: number } {
    const sx = nx * containerWidth;
    const sy = ny * containerHeight;
    const scx = cxNorm * containerWidth;
    const scy = cyNorm * containerHeight;
    const dx = sx - scx;
    const dy = sy - scy;
    return {
      x: (scx + dx * cos - dy * sin) / containerWidth,
      y: (scy + dx * sin + dy * cos) / containerHeight,
    };
  }

  const p1 = rotPt(shape.x1, shape.y1);
  const p2 = rotPt(shape.x2, shape.y2);

  return { ...shape, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, rotation: 0 };
}

function renderShape(
  shape: AnnotationShape,
  isEditor?: boolean,
  isSelected?: boolean,
  containerWidth?: number,
  containerHeight?: number,
) {
  // For lines/arrows with rotation, compute rotated endpoints directly (avoids blur)
  if (shape.type === 'line' && shape.rotation && containerWidth && containerHeight) {
    const rotated = rotateLineEndpoints(shape, shape.rotation, containerWidth, containerHeight);
    return <LineShape key={shape.id} shape={rotated} isEditor={isEditor} isSelected={isSelected} />;
  }
  if (shape.type === 'arrow' && shape.rotation && containerWidth && containerHeight) {
    const rotated = rotateLineEndpoints(shape, shape.rotation, containerWidth, containerHeight);
    return (
      <ArrowShape
        key={shape.id}
        shape={rotated}
        isEditor={isEditor}
        isSelected={isSelected}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
      />
    );
  }

  let element: React.ReactNode;
  switch (shape.type) {
    case 'box':
      element = <RectShape key={shape.id} shape={shape} isEditor={isEditor} isSelected={isSelected} />;
      break;
    case 'circle':
      element = <EllipseShape key={shape.id} shape={shape} isEditor={isEditor} isSelected={isSelected} />;
      break;
    case 'line':
      element = <LineShape key={shape.id} shape={shape} isEditor={isEditor} isSelected={isSelected} />;
      break;
    case 'arrow':
      element = (
        <ArrowShape
          key={shape.id}
          shape={shape}
          isEditor={isEditor}
          isSelected={isSelected}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
        />
      );
      break;
    case 'freeform':
      element = <FreeformShape key={shape.id} shape={shape} isEditor={isEditor} isSelected={isSelected} />;
      break;
    case 'text':
      element = <TextShape key={shape.id} shape={shape} isEditor={isEditor} isSelected={isSelected} />;
      break;
    default:
      return null;
  }

  // Wrap in rotation transform if shape has rotation
  if (shape.rotation && containerWidth && containerHeight) {
    const bounds = getShapeBounds(shape);
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    const transform = computeRotationTransform(shape.rotation, cx, cy, containerWidth, containerHeight);
    return (
      <g key={shape.id} transform={transform}>
        {element}
      </g>
    );
  }

  return element;
}

export const SvgCanvas = React.memo(function SvgCanvas({
  shapes,
  drawingShape,
  isEditor,
  selectedIds,
  containerWidth,
  containerHeight,
  children,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
}: SvgCanvasProps) {
  return (
    <svg
      className="kollab-annotation-canvas"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      overflow="hidden"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
    >
      {shapes.map((shape) => renderShape(shape, isEditor, selectedIds?.has(shape.id), containerWidth, containerHeight))}
      {drawingShape && renderShape(drawingShape, isEditor, false, containerWidth, containerHeight)}
      {children}
    </svg>
  );
});
